/**
 * PlannedTaskGroup Component
 *
 * 任務分組容器，顯示標題和可收合的任務列表。
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Task } from '../../../types/task';
import PlannedTaskCard from './PlannedTaskCard';
import { ChevronDown, ChevronRight } from 'lucide-react';

export interface PlannedTaskGroupProps {
  /** 分組標題 */
  title: string;
  /** 任務列表 */
  tasks: Task[];
  /** 是否預設展開 */
  defaultExpanded?: boolean;
  /** 標題圖標 */
  icon?: React.ReactNode;
  /** 標題樣式類 */
  titleClassName?: string;
  /** 任務數量的樣式 */
  countClassName?: string;
  /** 是否可拖曳 */
  draggable?: boolean;
  /** 拖曳開始回調（滑鼠） */
  onDragStart?: (task: Task, e: React.DragEvent) => void;
  /** 拖曳結束回調 */
  onDragEnd?: () => void;
  /** 觸控拖曳開始回調（平板） */
  onTouchStart?: (task: Task, e: React.TouchEvent) => void;
  /** 點擊排程回調 */
  onQuickSchedule?: (task: Task) => void;
  /** 進入放置模式回調 */
  onEnterPlacementMode?: (task: Task) => void;
  /** 點擊查看詳情 */
  onViewDetail?: (task: Task) => void;
  /** 是否使用緊湊模式 */
  compact?: boolean;
  /** 高亮指定 Task ID */
  highlightTaskId?: string | null;
  /** 清除高亮回調 */
  onClearHighlight?: () => void;
}

const PlannedTaskGroup: React.FC<PlannedTaskGroupProps> = ({
  title,
  tasks,
  defaultExpanded = true,
  icon,
  titleClassName = '',
  countClassName = 'bg-gray-100 text-gray-600',
  draggable = false,
  onDragStart,
  onDragEnd,
  onTouchStart,
  onQuickSchedule,
  onEnterPlacementMode,
  onViewDetail,
  compact = false,
  highlightTaskId,
  onClearHighlight,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // 如果高亮的 Task 在此分組中，自動展開
  const hasHighlightedTask = highlightTaskId
    ? tasks.some(t => t.id === highlightTaskId)
    : false;

  useEffect(() => {
    if (hasHighlightedTask) {
      setIsExpanded(true);
    }
  }, [hasHighlightedTask]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // 沒有任務時不顯示
  if (tasks.length === 0) {
    return null;
  }

  return (
    <div className="mb-4">
      {/* 分組標題 */}
      <button
        onClick={toggleExpanded}
        className={`
          w-full flex items-center justify-between
          py-2 px-1 rounded-md
          hover:bg-gray-50 transition-colors
          ${titleClassName}
        `}
      >
        <div className="flex items-center gap-2">
          {/* 展開/收合圖標 */}
          <span className="text-gray-400">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </span>

          {/* 自定義圖標 */}
          {icon && <span className="text-gray-500">{icon}</span>}

          {/* 標題文字 */}
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>

        {/* 任務數量 */}
        <span
          className={`
            text-xs px-2 py-0.5 rounded-full
            ${countClassName}
          `}
        >
          {tasks.length}
        </span>
      </button>

      {/* 任務列表 */}
      {isExpanded && (
        <div className="mt-2 space-y-2 pl-1">
          {tasks.map((task) => (
            <PlannedTaskCard
              key={task.id}
              task={task}
              draggable={draggable}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onTouchStart={onTouchStart}
              onQuickSchedule={onQuickSchedule}
              onEnterPlacementMode={onEnterPlacementMode}
              onViewDetail={onViewDetail}
              compact={compact}
              isHighlighted={task.id === highlightTaskId}
              onClearHighlight={onClearHighlight}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PlannedTaskGroup;
